import { Button, CardBody, CardHeader, Card, Text } from '@change/ds-core-ui';
import { SparkleIcon } from '@change/ds-icons';

import { TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { CtaSection, DecisionMakerSection, VoiceHeardSection } from '#src/app/shared/components/socialStarter';

import { Loading } from '../PersonalPrompt/components/Loading';

import { useChatAppEntry } from './hooks/useChatAppEntry';

/**
 * Landing step for users arriving from the Chat App with prefilled issue and
 * personal story. Displays the prefilled content as read-only; the user
 * cannot edit. The CTA submits the prefilled draft to trigger AI draft
 * generation and advance to the preview step.
 */
export const ChatAppEntry: React.FC = () => {
	const { translate } = useI18n();
	const { data, actions } = useChatAppEntry();

	if (data.loading) return <Loading />;

	return (
		<div className="flex flex-col pt-8 md:pt-20" data-qa="chat-app-entry">
			<div className="container flex flex-col gap-10 md:gap-17">
				<div className="flex flex-col gap-4 px-4 text-center md:px-0">
					<Text as="h1" variant="heading">
						<TranslateHtml value="corgi.start_a_petition.chat_app_entry.heading" />
					</Text>
					<Text as="p" size="large">
						<TranslateHtml value="corgi.start_a_petition.chat_app_entry.description" />
					</Text>
				</div>

				<div className="mx-auto flex w-full max-w-87.5 flex-col items-center gap-6">
					<Card
						orientation="vertical"
						className="elevation-elevated-selected bg-fill-inverse-strong w-full [--card-gap:0] [--card-padding:0]"
						data-qa="chat-app-entry-preview-card"
					>
						<CardHeader className="bg-fill-strong px-10! py-10">
							<div className="bg-fill-brand-strong mb-4 h-2 w-13" />
							<Text variant="heading" as="p" className="text-typography-inverse-strong" size="small">
								{data.problem}
							</Text>
						</CardHeader>

						<CardBody className="flex flex-col gap-4 px-8! py-8">
							<div className="bg-background-overlay h-4 w-full rounded-md" />
							<div className="bg-background-overlay h-4 w-full rounded-md" />
						</CardBody>
					</Card>

					<Button
						className="w-full"
						variant="primary"
						leadingIcon={<SparkleIcon />}
						data-qa="chat-app-entry-cta-button"
						onClick={actions.submitFromMainCta}
					>
						{translate('corgi.start_a_petition.chat_app_entry.cta_button')}
					</Button>
				</div>
			</div>

			<div className="bg-background-raised mt-26 pb-20">
				<VoiceHeardSection className="-mt-26" connector="tall" />
				<DecisionMakerSection connector="tall" />
			</div>

			<CtaSection
				onCtaClick={actions.submitFromFooterCta}
				onCtaView={actions.trackCtaSectionView}
				headingText={<TranslateHtml value="corgi.start_a_petition.chat_app_entry.cta_heading" />}
				buttonText={translate('corgi.start_a_petition.chat_app_entry.cta_button')}
				qaId="chat-app-entry-footer-cta-button"
			/>
		</div>
	);
};
